import {
  ArrowLeftOutlined,
  DownOutlined,
  EyeOutlined,
  GlobalOutlined,
  PictureOutlined,
  SaveOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Modal, Select, Switch, Tag } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { MediaPickerModal } from '@/components/MediaPickerModal/MediaPickerModal';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useCreateBlogMutation,
  useGetBlogQuery,
  useUpdateBlogMutation,
} from '@/modules/blogs/blogsApi';
import type { BlogPost } from '@/types/blog';
import { resolveMutationResult } from '@/utils/apiResponse';
import { getMediaImageUrl } from '@/utils/media';
import { BlogArticleView } from './components/BlogArticleView';
import styles from './BlogForm.module.scss';

interface BlogFormValues {
  title: string;
  slug: string;
  content: string;
  coverImage: string;
  tags: string[];
  isPublished: boolean;
  publishedAt?: Dayjs | null;
  seoTitle: string;
  seoDescription: string;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function BlogForm() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { data: post, isLoading: isLoadingPost } = useGetBlogQuery(id ?? '', {
    skip: !isEdit || !id,
  });
  const [createBlog, createState] = useCreateBlogMutation();
  const [updateBlog, updateState] = useUpdateBlogMutation();
  const isSaving = createState.isLoading || updateState.isLoading;

  useGlobalLoader(isSaving);
  useGlobalLoader(isEdit && isLoadingPost);

  const [isSlugManual, setIsSlugManual] = useState(false);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [seoCollapsed, setSeoCollapsed] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { control, handleSubmit, reset, watch, setValue, getValues } = useForm<BlogFormValues>({
    defaultValues: {
      title: '',
      slug: '',
      content: '',
      coverImage: '',
      tags: [],
      isPublished: false,
      publishedAt: null,
      seoTitle: '',
      seoDescription: '',
    },
  });

  const title = watch('title');
  const slug = watch('slug');
  const content = watch('content');
  const coverImage = watch('coverImage');
  const tags = watch('tags');
  const isPublished = watch('isPublished');
  const publishedAt = watch('publishedAt');
  const seoTitle = watch('seoTitle');
  const seoDescription = watch('seoDescription');
  const hasSeoValues = Boolean(seoTitle || seoDescription);

  const slugPreview = useMemo(() => slugify(title), [title]);

  useEffect(() => {
    if (isEdit || isSlugManual) return;
    setValue('slug', slugPreview);
  }, [isEdit, isSlugManual, setValue, slugPreview]);

  useEffect(() => {
    if (!isEdit || !post) return;

    setIsSlugManual(true);
    reset({
      title: post.title,
      slug: post.slug,
      content: post.content,
      coverImage: post.coverImage,
      tags: post.tags,
      isPublished: post.isPublished,
      publishedAt: post.publishedAt ? dayjs(post.publishedAt) : null,
      seoTitle: post.seoTitle,
      seoDescription: post.seoDescription,
    });
  }, [isEdit, post, reset]);

  const buildPayload = (values: BlogFormValues) => ({
    title: values.title.trim(),
    slug: values.slug.trim() || slugify(values.title),
    content: values.content,
    coverImage: values.coverImage.trim(),
    tags: values.tags,
    isPublished: values.isPublished,
    ...(values.publishedAt ? { publishedAt: values.publishedAt.toISOString() } : {}),
    seoTitle: values.seoTitle.trim(),
    seoDescription: values.seoDescription.trim(),
  });

  const previewArticle = useMemo(
    () => ({
      title: title.trim() || 'Untitled article',
      slug: slug.trim() || slugPreview,
      content,
      coverImage,
      tags,
      isPublished,
      publishedAt: publishedAt?.toISOString() ?? null,
    }),
    [content, coverImage, isPublished, publishedAt, slug, slugPreview, tags, title],
  );

  const onSubmit = async (values: BlogFormValues) => {
    const payload = buildPayload(values);

    const result =
      isEdit && id
        ? await resolveMutationResult<BlogPost>(updateBlog({ id, body: payload }))
        : await resolveMutationResult<BlogPost>(createBlog(payload));

    if (result.ok) {
      notify.success(result.message ?? `Blog post ${isEdit ? 'updated' : 'created'}.`);

      if (isEdit && id) {
        navigate(`/blogs/${id}/preview`);
        return;
      }

      navigate(`/blogs/${result.data.id}/preview`, { state: { fromCreate: true } });
      return;
    }

    notify.error(result.message);
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.BLOGS)}>
            Back to blogs
          </Button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className={styles.shell}>
        <aside className={styles.sidePanel}>
          <div className={styles.panelTop}>
            <span className={styles.panelLabel}>Article details</span>
          </div>

          <div className={styles.sideBody}>
            <Form.Item label="Slug" required className={styles.field}>
              <Controller
                name="slug"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="article-slug"
                    onChange={(event) => {
                      setIsSlugManual(true);
                      field.onChange(event);
                    }}
                  />
                )}
              />
            </Form.Item>

            <Form.Item label="Publish date" className={styles.field}>
              <Controller
                name="publishedAt"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    showTime
                    className={styles.fullWidth}
                    value={field.value ?? null}
                    onChange={field.onChange}
                  />
                )}
              />
            </Form.Item>

            <Form.Item label="Cover image" className={styles.field}>
              <div className={styles.coverCard}>
                {coverImage ? (
                  <div className={styles.coverImageWrap}>
                    <img
                      src={getMediaImageUrl(coverImage)}
                      alt="Cover"
                      className={styles.coverPreviewImg}
                    />
                  </div>
                ) : (
                  <div className={styles.coverPlaceholder}>
                    <PictureOutlined />
                    <span>No cover selected</span>
                  </div>
                )}
                <div className={styles.coverActions}>
                  <Button block icon={<PictureOutlined />} onClick={() => setCoverPickerOpen(true)}>
                    {coverImage ? 'Change cover' : 'Select cover'}
                  </Button>
                  {coverImage ? (
                    <Button type="link" danger block onClick={() => setValue('coverImage', '')}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              </div>
              <Controller
                name="coverImage"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
            </Form.Item>

            <Form.Item label="Tags" className={styles.field}>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <Select
                    mode="tags"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Add tags"
                    tokenSeparators={[',']}
                    tagRender={(props) => <Tag {...props}>{props.label}</Tag>}
                  />
                )}
              />
            </Form.Item>
          </div>
        </aside>

        <section className={styles.editorPanel}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarMain}>
              <Controller
                name="title"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    className={styles.titleInput}
                    placeholder="Article title"
                    bordered={false}
                  />
                )}
              />
              <Tag color={isPublished ? 'success' : 'default'} className={styles.statusTag}>
                {isPublished ? 'Published' : 'Draft'}
              </Tag>
            </div>

            <div className={styles.toolbarActions}>
              <div className={styles.publishToggle}>
                <span>Published</span>
                <Controller
                  name="isPublished"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onChange={(checked) => {
                        field.onChange(checked);
                        if (checked && !getValues('publishedAt')) {
                          setValue('publishedAt', dayjs());
                        }
                      }}
                    />
                  )}
                />
              </div>
              <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
                Preview
              </Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={isSaving}>
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>

          <div className={styles.contentArea}>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Write article content..."
                  height={520}
                  onUploadError={(message) => notify.error(message)}
                />
              )}
            />
          </div>

          <div className={styles.seoSection} data-collapsed={seoCollapsed ? 'true' : 'false'}>
            <button
              type="button"
              className={styles.seoHeader}
              onClick={() => setSeoCollapsed((value) => !value)}
              aria-expanded={!seoCollapsed}
            >
              <span className={styles.seoHeaderMain}>
                <GlobalOutlined />
                <span className={styles.panelLabel}>SEO</span>
                {seoCollapsed && hasSeoValues ? (
                  <span className={styles.seoFilledDot} aria-hidden />
                ) : null}
              </span>
              <span className={styles.seoChevron}>
                {seoCollapsed ? <DownOutlined /> : <UpOutlined />}
              </span>
            </button>

            {!seoCollapsed ? (
              <div className={styles.seoBody}>
                <p className={styles.seoHint}>
                  Search engine title and description for this article.
                </p>

                <div className={styles.seoStack}>
                  <Form.Item label="SEO title" className={styles.field}>
                    <Controller
                      name="seoTitle"
                      control={control}
                      render={({ field }) => <Input {...field} placeholder="SEO title" />}
                    />
                  </Form.Item>

                  <Form.Item label="SEO description" className={styles.field}>
                    <Controller
                      name="seoDescription"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} placeholder="Short description for search engines" />
                      )}
                    />
                  </Form.Item>
                </div>

                {(seoTitle || seoDescription) && (
                  <div className={styles.seoPreview}>
                    <span className={styles.previewLabel}>Preview</span>
                    <div className={styles.previewTitle}>{seoTitle || title || 'Page title'}</div>
                    <div className={styles.previewDesc}>
                      {seoDescription || 'SEO description will appear here.'}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </section>
      </form>

      <Modal
        title="Article preview"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={920}
        destroyOnClose
        className={styles.previewModal}
      >
        <BlogArticleView article={previewArticle} />
      </Modal>

      <MediaPickerModal
        open={coverPickerOpen}
        value={[]}
        onClose={() => setCoverPickerOpen(false)}
        onChange={(_ids, items) => {
          const image = items[0]?.imageLarge ?? items[0]?.imageSmall ?? '';
          setValue('coverImage', image);
          setCoverPickerOpen(false);
        }}
      />
    </div>
  );
}
